import { useEffect, useState } from "react";
import Cleave from "cleave.js/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { CreditCardIcon } from "@heroicons/react/24/solid";
import { CalendarIcon, LockClosedIcon } from "@heroicons/react/16/solid";

declare global {
    interface Window {
        postData: () => Promise<{ payment_token_id: string }>;
    }
}

const cardIcons: Record<string, string> = {
    visa: "visa",
    mastercard: "mastercard",
    amex: "amex",
    discover: "discover",
    diners: "diners",
    unknown: "Card",
};

interface Props {
    onClose: () => void;
    onCardValidationSuccess: (res:any) => void;
    freedomURL: string;
    secretKey: string;
}

export default function CreditCardFormModal({

                                                onClose,
                                                onCardValidationSuccess,
                                                freedomURL,
                                                secretKey,
                                            }: Props) {
    const [postData, setPostData] = useState<any>(null);
    const [cardType, setCardType] = useState("unknown");
    const [cardNumber, setCardNumber] = useState("");
    const [expiry, setExpiry] = useState("");
    const [cvv, setCvv] = useState("");
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    useEffect(() => {

        if (!document.getElementById("ftd")) {
            const script = document.createElement("script");
            script.src = freedomURL;
            script.id = "ftd";
            script.setAttribute("security_key", secretKey);

            script.onload = () => {
                if (typeof window.postData === "function") {
                    setPostData(() => window.postData);
                } else {
                    setError("postData not found.");
                }
            };

            script.onerror = () => setError("Failed to load script.");
            document.body.appendChild(script);
        } else {
            setPostData(() => window.postData);
        }
    }, [freedomURL, secretKey]);

    const validateExpiry = (val: string) => {
        const [month, year] = val.split("/");
        if (!month || !year) return false;
        const now = new Date();
        const exp = new Date(Number(`20${year}`), Number(month) - 1, 1);
        return exp >= now;
    };

    const validateCvv = (val: string) => {
        if (cardType === "amex") {
            return /^\d{4}$/.test(val);
        }
        return /^\d{3}$/.test(val);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");

        if (!postData) return setError("Payment script not loaded");
        if (!validateExpiry(expiry)) return setError("expired card");
        if (!validateCvv(cvv)) return setError("Invalid CVV for this card");

        try {
            setIsSubmitting(true);
            const res = await postData();
            if (!res.payment_token_id) {
                // onCardValidationSuccess({ccCvv:"***",
                //     ccExpiry:"**/**",
                //     ccNumber:"4242********4242",
                //     errorField:null,
                //     message:"Success",
                //     payment_token_id:"1b2e4794-ffcc-4e07-9c05-c4488c74c92f",
                //     statusCode:"00",status:true});
                onCardValidationSuccess({status:false,error:res.message || "Card validation failed"});
                setError(res.message || "Card validation failed");
            } else {
                setCardNumber(res.ccNumber);
                setIsSubmitting(false);
                onClose();
                onCardValidationSuccess({...res,status:true});
            }
        } catch {
            setError("Card validation failed.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="flex items-center justify-center px-4">
            <div className="bg-white w-full max-w-md p-6 rounded-2xl shadow-2xl relative space-y-5">
                {/* Close Icon */}
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 transition"
                    aria-label="Close"
                >
                    <XMarkIcon className="w-5 h-5" />
                </button>

                <form onSubmit={handleSubmit}>
                    <div>
                        <h2 className="py-2">Enter Card Details</h2>
                        <div className="relative">
                            <CreditCardIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                            <Cleave
                                placeholder="Card Number"
                                options={{
                                    creditCard: true,
                                    onCreditCardTypeChanged: (type: string) =>
                                        setCardType(type || "unknown"),
                                }}
                                id="ccnumber"
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                                required
                                onChange={(e:any) => setCardNumber(e.target.value)}
                            />
                        </div>
                        <p className="text-xs text-gray-500 pl-1 mt-1">
                            {cardType !== "unknown" ? (
                                <strong>{cardIcons[cardType]}</strong>
                            ) : (
                                <></>
                            )}
                        </p>
                    </div>

                    <div className="flex gap-3 mt-4">
                        <div className="relative w-1/2">
                            <CalendarIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                            <Cleave
                                placeholder="MM/YY"
                                options={{ date: true, datePattern: ["m", "y"] }}
                                onChange={(e:any) => setExpiry(e.target.value)}
                                id="ccexpiry"
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                                required
                            />
                        </div>

                        <div className="relative w-1/2">
                            <LockClosedIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                            <input
                                type="password"
                                inputMode="numeric"
                                placeholder="CVV"
                                maxLength={4}
                                value={cvv}
                                onChange={(e) => setCvv(e.target.value)}
                                id="cccvv"
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                                required
                            />
                        </div>
                    </div>

                    {error && <p className="text-red-500 text-sm mt-3">{error}</p>}

                    <button
                        type="submit"
                        disabled={!cardNumber || !expiry || !cvv || isSubmitting}
                        className={`w-full py-2 rounded-md transition text-sm font-medium mt-5 flex justify-center items-center ${
                            !cardNumber || !expiry || !cvv || isSubmitting
                                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                : "bg-blue-600 text-white hover:bg-blue-700"
                        }`}
                    >
                        {isSubmitting ? (
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                            "Add Credit Card"
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}
